import React, { useMemo, useState } from "react";
import { connect, useDispatch } from 'react-redux';
import ContentSections from "./ContentSections";
import i18n from 'I18n';
import { useFormContext } from "react-hook-form";
import { SectionTopTitle } from "./CommonComponent";
import Loading from "./Loading";
import { LanguageConfig } from "config/CustomEnums";
import './BaseCreationStepContentByConfig.scss';

const getLanguageString = (name, language, hideInOtherLanguages) => {
  if (hideInOtherLanguages) {
    return name;
  };
  
  return i18n.t(name, { locale: language, defaultValue: name });
}

const mapPropsToState = (state) => ({
  languages: state.language.allList,
});

const BaseCreationStepContentByConfig = ({
  configuration,
  languages,
  isLoading = false,
  ...otherProps
}) => {
  const formContext = useFormContext();
  const {
    isConfigByKey = false,
    getSectionKey,
    getDisplayComponentKeys,
    languageExtraTopSection,
    languageSections: languageSectionConfig,
    normalSections: normalSectionConfig,
  } = configuration

  const {
    displayNormalSectionComponents,
    displayLanguageSectionComponents
  } = getDisplayComponentKeys({ formContext });
  const sectionKey = isConfigByKey ? getSectionKey({ formContext }) : null

  const [normalSections, languageSections] = useMemo(() => {
    if (isConfigByKey) {
      return [normalSectionConfig?.[sectionKey] || [], languageSectionConfig?.[sectionKey] || []]
    } 
    return [normalSectionConfig || [], languageSectionConfig || []]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigByKey, sectionKey])

  const [activeSection, setActiveSection] = useState();
  const dispatch = useDispatch();

  const getNormalSections = () => {
    if (isLoading) {
      return [<Loading />]
    }
    return normalSections?.map((section) => {
      const sectionDisplayComponents = section?.sectionContent?.filter(
        (componentConfig) => displayNormalSectionComponents?.includes(componentConfig?.key)
      ) || []
      if (sectionDisplayComponents.length < 1) {
        return null
      }
      const displaySectionTitle = section?.sectionTitle ||
        (section?.getDisplaySectionTitle && section.getDisplaySectionTitle({ formContext }))
      const displaySectionDescription = section?.description ||
        (section?.getDisplaySectionDescription && section.getDisplaySectionDescription({ formContext }))
      return (
        <>
          {section?.sectionKey && <div id={section?.sectionKey} className="section-id-none" />}
          {displaySectionTitle && <SectionTopTitle title={displaySectionTitle} />}
          {displaySectionDescription &&
            <label className={`section-short-description ${section?.descriptionClass || ""}`}>
              {displaySectionDescription}
            </label>
          }
          {sectionDisplayComponents.map((componentConfig) => {
            if (componentConfig?.complexComponent) {
              return (
                <>
                  <div id={componentConfig.key} className="section-id-none" />
                  {
                    componentConfig.complexComponent({
                      setActiveSection,
                      dispatch,
                      formContext,
                    })
                  }
                </>
              )
            }
            return componentConfig?.component ? (
              <>
                <div id={componentConfig.key} className="section-id-none" />
                <componentConfig.component
                  focusChange={setActiveSection}
                  {...componentConfig?.componentProps}
                />
              </>
            )
              : null
          })}
        </>
      )
    })
  }

  const getLanguageTabs = (language) => {
    if (isLoading) {
      return [<Loading />]
    }
    return languageSections?.map((sectionConfig) => {
      const sectionDisplayComponents = sectionConfig?.sectionContent?.filter(
        (componentConfig) => displayLanguageSectionComponents?.includes(componentConfig?.key)
      ) || []
      if (sectionDisplayComponents.length < 1) {
        return null
      }

      if (sectionConfig.hideInOtherLanguages && language !== LanguageConfig.english) {
        return null;
      };

      const displaySectionTitle = sectionConfig?.sectionTitle ||
        (sectionConfig?.getDisplaySectionTitle && sectionConfig.getDisplaySectionTitle({ formContext }))

      const displaySectionDescription = sectionConfig?.description ||
        (sectionConfig?.getDisplaySectionDescription && sectionConfig.getDisplaySectionDescription({ formContext }))
      return (
        <>
          {sectionConfig?.sectionKey && <div id={sectionConfig?.sectionKey} className="section-id-none" />}
          {displaySectionTitle
            && <SectionTopTitle
              title={getLanguageString(displaySectionTitle, language, sectionConfig.hideInOtherLanguages)}
              popoverDetail={sectionConfig?.sectionTitlePopoverDetail}
              popoverImage={sectionConfig?.sectionTitlePopoverImage}
            />}
          {displaySectionDescription &&
            <label className={`section-short-description ${sectionConfig?.descriptionClass || ""}`}>
              {getLanguageString(displaySectionDescription, language, sectionConfig.hideInOtherLanguages)}
            </label>
          }
          {sectionDisplayComponents.map(fieldInput => {
            if (fieldInput?.complexComponent) {
              return (
                <>
                  <div id={fieldInput.key} className="section-id-none" />
                  {
                    fieldInput.complexComponent({
                      languages,
                      language,
                      focusChange: setActiveSection,
                      setActiveSection,
                      dispatch,
                      formContext,
                    })
                  }
                </>
              )
            }
            return fieldInput?.component ?
              <>
                <div id={fieldInput.key} className="section-id-none" />
                <fieldInput.component
                  focusChange={setActiveSection}
                  language={language}
                  {...fieldInput?.componentProps}
                />
              </>
              : null
          })}
        </>)
    })
  };
  return <ContentSections
    sections={getNormalSections()}
    languageExtraTopSection={() => {
      if (!languageExtraTopSection) {
        return null;
      };
      return languageExtraTopSection({ formContext })
    }}
    languageTabContent={displayLanguageSectionComponents?.length > 0 ? {
      containers: languages?.map((language) => {
        return {
          container: getLanguageTabs(language.code),
          key: language.code,
          title: language.sourceName,
        };
      }),
    } : null}
    activeSection={activeSection}
    {...otherProps}
  />
}

export default connect(mapPropsToState)(BaseCreationStepContentByConfig);

